import React, { ReactNode } from 'react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { Heading, MinorHeading, Subtitle } from '../shared/typography/PrimaryTypography';
import PrimaryTabs from '../shared/tab/PrimaryTabs';
import BorderContainer from '../shared/container/BorderContainer';
import PrimaryTab from '../shared/tab/PrimaryTab';
import syntiro_icon_background from '../../assets/syntiro-logo-no-text.svg';
import sustainability_features from '../../assets/landing_page/sustainability-features.svg';
import utility_management_features from '../../assets/landing_page/utility-management-features.svg';
import asset_management_features from '../../assets/landing_page/asset-management-features.svg';

interface ModuleSolutionData {
  subtitle: string;
  bullets: Array<ReactNode>;
  image: string;
}

const HighlightTag = ({ children }: { children: ReactNode }) => (
  <span style={{ fontWeight: 600, fontSize: 'inherit', color: '#FF730B', display: 'inline-block' }}>
    {children}
  </span>
);

const BulletList = ({ items }: { items: Array<ReactNode> }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {items.map((item, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#1A9E8F',
            flexShrink: 0
          }}
        />
        <MinorHeading sx={{ color: '#7D8F99', lineHeight: 1.6 }}>{item}</MinorHeading>
      </Box>
    ))}
  </Box>
);

/**
 * Section describing key app features in landing page.
 * @returns {JSX.Element}
 */
const SolutionsSection = () => {
  const [tabValue, setTabValue] = React.useState<number>(0);
  const mobile_viewport: boolean = useMediaQuery('(max-width:600px)');

  const handleChangeTab = (value: number): void => {
    setTabValue(value);
  };

  const module_solutions: Array<ModuleSolutionData> = [
    {
      subtitle: 'ESG, compliance and reporting with audit-grade data.',
      bullets: [
        <>
          Consolidate over <HighlightTag>20 000 data points</HighlightTag> into a single integrated
          sustainability report
        </>,
        <>
          Track more than <HighlightTag>300 ESG metrics</HighlightTag> across major frameworks
        </>,
        <>
          Monitor <HighlightTag>Scope 1, 2 and Scope 3 emissions</HighlightTag>
        </>,
        <>Produce accurate, audit-ready disclosures</>
      ],
      image: sustainability_features
    },
    {
      subtitle: 'Turn utility data into measurable financial performance.',
      bullets: [
        <>
          Identify <HighlightTag>tariff optimisation</HighlightTag> opportunities
        </>,
        <>
          Reduce energy intensity by up to <HighlightTag>38%</HighlightTag>
        </>,
        <>Prioritise high-impact energy projects</>,
        <>Clean and structure complex meter data automatically</>,
        <>
          <HighlightTag>Integrate</HighlightTag> with AMR and multiple data sources
        </>
      ],
      image: utility_management_features
    },
    {
      subtitle: 'Operate energy infrastructure with real-time performance visibility.',
      bullets: [
        <>
          Monitor <HighlightTag>100 000+ live operational data points</HighlightTag>
        </>,
        <>Track energy flows across supply and demand systems</>,
        <>
          <HighlightTag>Detect deviations</HighlightTag> across refrigeration, HVAC and energy
          systems
        </>,
        <>Improve asset efficiency and system health</>,
        <>
          <HighlightTag>Optimise energy consumption</HighlightTag> and emissions across your
          portfolio
        </>
      ],
      image: asset_management_features
    }
  ];
  const current_module_data: ModuleSolutionData = module_solutions[tabValue];

  return (
    <Box id="solutions">
      <Grid
        container
        spacing={4}
        sx={{ alignItems: 'center', justifyContent: 'center', px: { xs: 2, lg: 12 } }}
      >
        <Grid size={12}>
          {mobile_viewport ? (
            <Heading weight="Bold">Platform Capabilities</Heading>
          ) : (
            <Subtitle weight="Bold">Platform Capabilities</Subtitle>
          )}
        </Grid>
        <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <PrimaryTabs
            value={tabValue}
            onChange={(event, new_tab: number) => handleChangeTab(new_tab)}
            sx={{ width: '100%', alignItems: 'center', justifyContent: 'center' }}
          >
            <PrimaryTab
              label="Sustainability"
              sx={{ ml: { xs: 0, md: 'auto' }, minWidth: { xs: 300, md: 250 } }}
            />
            <PrimaryTab label="Utility Management" sx={{ minWidth: { xs: 300, md: 250 } }} />
            <PrimaryTab
              label="Asset Management"
              sx={{ mr: { xs: 0, md: 'auto' }, minWidth: { xs: 300, md: 250 } }}
            />
          </PrimaryTabs>
        </Grid>
        <Grid size={12}>
          {current_module_data && (
            <BorderContainer
              sx={{
                textAlign: 'left',
                background: `linear-gradient(rgba(255, 255, 255, 0.9), 
              rgba(255, 255, 255, 0.9)),url(${syntiro_icon_background});`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPosition: {
                  xs: 'center calc(100% + 100px)',
                  md: 'left 250px'
                }
              }}
            >
              <Grid container spacing={6} sx={{ alignItems: 'center', p: 2 }}>
                <Grid
                  size={{ xs: 12, md: 5 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    order: { xs: 2, md: 1 }
                  }}
                >
                  <img
                    style={{ maxWidth: '100%', maxHeight: 450 }}
                    src={current_module_data.image}
                    alt="#"
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, md: 7 }}
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    order: { xs: 1, md: 2 }
                  }}
                >
                  <Box>
                    <Heading weight="Bold" sx={{ mb: 2 }}>
                      {current_module_data.subtitle}
                    </Heading>
                    <BulletList items={current_module_data.bullets} />
                  </Box>
                </Grid>
              </Grid>
            </BorderContainer>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SolutionsSection;
